// app/components/resources.jsx
import React, { useState } from 'react'
import { Link, useLocation } from '@remix-run/react'
import { ArrowUpIcon, MagnifyingGlassIcon } from '@heroicons/react/24/outline'
import SharePopover from './sharePopover'
import { Await } from '@remix-run/react'
import { Suspense } from 'react'
import Loading from './loading'

const Resources = ({ initialCategory, color, groupByCategory = false, resources, categories }) => {
  const [activeCategory, setActiveCategory] = useState('')
  const [searchQuery, setSearchQuery] = useState('')
  const location = useLocation()

  const filterResources = (resolvedResources) => {
    const categoryFilter = activeCategory
      ? resolvedResources.filter((resource) => resource.category === activeCategory)
      : resolvedResources
    const searchFilter = searchQuery
      ? categoryFilter.filter((resource) => resource.title.toLowerCase().includes(searchQuery.toLowerCase()))
      : categoryFilter
    return searchFilter
  }

  const handleCategoryClick = (category) => {
    setActiveCategory(category)
    setSearchQuery('')
  }

  const handleSearch = (e) => {
    setSearchQuery(e.target.value)
  }

  return (
    <section className="tl-container">
      <Suspense fallback={<Loading />}>
        <Await resolve={categories} errorElement={<div>Failed to load. Please try again later.</div>}>
          {(categories) => (
            <>
              {initialCategory ? null : (
                <div className="flex flex-col lg:flex-row justify-between items-center mb-8">
                  <div className="flex space-x-4 md:space-x-8 mb-4 md:mb-0 flex-wrap">
                    <button
                      className={`text-sm md:text-lg font-semibold ${
                        !activeCategory ? 'text-pointed' : 'text-gray-500'
                      }`}
                      onClick={() => handleCategoryClick('')}
                    >
                      All
                    </button>
                    {categories.map((category) => (
                      <button
                        key={category}
                        className={`${
                          activeCategory === category ? 'text-pointed' : 'text-gray-500'
                        } text-sm md:text-lg font-semibold`}
                        onClick={() => handleCategoryClick(category)}
                      >
                        {category}
                      </button>
                    ))}
                  </div>
                  <div className="relative w-full lg:w-auto lg:ml-10">
                    <input
                      type="text"
                      placeholder="Search"
                      className="border border-gray-300 rounded-full py-2 px-4 pl-10 w-full xl:w-64"
                      value={searchQuery}
                      onChange={handleSearch}
                    />
                    <MagnifyingGlassIcon className="w-5 h-5 text-gray-400 absolute top-2 left-3" />
                  </div>
                </div>
              )}

              <Suspense fallback={<Loading />}>
                <Await resolve={resources} errorElement={<div>Failed to load. Please try again later.</div>}>
                  {(resources) => {
                    const filteredResources = filterResources(resources)

                    if (groupByCategory) {
                      // Group resources by category
                      const groupedResources = filteredResources.reduce((acc, resource) => {
                        const key = resource.category || 'Uncategorized'
                        if (!acc[key]) {
                          acc[key] = []
                        }
                        acc[key].push(resource)
                        return acc
                      }, {})

                      return (
                        <div className="space-y-8">
                          {Object.entries(groupedResources).map(([groupedCategory, resources]) => (
                            <div key={groupedCategory}>
                              {/* Sub-category heading */}
                              <h2 className="text-2xl font-bold my-4">{groupedCategory}</h2>
                              {/* Resources under the sub-category */}
                              <div className="space-y-4">
                                {resources.map((resource) => {
                                  const resourceDate = new Date(resource.date).toLocaleDateString('en-US', {
                                    year: 'numeric',
                                    month: 'long',
                                    timeZone: 'UTC',
                                  })

                                  return (
                                    <div
                                      key={resource.id}
                                      className={`group flex flex-col lg:flex-row lg:items-center justify-between p-4 border rounded-lg shadow-sm ${
                                        color ? color : 'bg-grey5'
                                      } hover:bg-grey10`}
                                    >
                                      <Link
                                        to={resource.link}
                                        state={{ from: location.pathname }}
                                        className="flex flex-col lg:flex-row lg:items-center w-full"
                                        target="_blank"
                                        rel="noopener noreferrer"
                                      >
                                        <div className="mr-0 md:mr-4 mb-2 md:mb-0 lg:self-center lg:p-2 flex-shrink-0 lg:mr-4">
                                          <span className="text-sm bg-pointed text-white rounded-full py-1 px-3 inline-flex max-w-full">
                                            {resource.sub_category}
                                          </span>
                                          <span className="block text-sm text-gray-500 mt-1">{resourceDate}</span>
                                        </div>
                                        <span className="text-lg font-semibold flex-grow">{resource.title}</span>
                                      </Link>
                                      <div className="flex items-center space-x-4 mt-2 md:mt-0">
                                        <SharePopover url={resource.link} />
                                        <Link to={resource.link} target="_blank">
                                          <ArrowUpIcon className="w-6 h-6 text-pointed cursor-pointer rotate-45 group-hover:rotate-90" />
                                        </Link>
                                      </div>
                                    </div>
                                  )
                                })}
                              </div>
                            </div>
                          ))}
                        </div>
                      )
                    } else {
                      // Render resources as a flat list
                      return (
                        <div className="space-y-4">
                          {filteredResources.map((resource) => {
                            const resourceDate = new Date(resource.date).toLocaleDateString('en-US', {
                              year: 'numeric',
                              month: 'long',
                              timeZone: 'UTC',
                            })

                            return (
                              <div
                                key={resource.id}
                                className={`group flex flex-col lg:flex-row lg:items-center justify-between p-4 border rounded-lg shadow-sm ${
                                  color ? color : 'bg-grey5'
                                } hover:bg-grey10`}
                              >
                                <Link
                                  to={resource.link}
                                  state={{ from: location.pathname }}
                                  className="flex flex-col lg:flex-row lg:items-center w-full"
                                  target="_blank"
                                  rel="noopener noreferrer"
                                >
                                  <div className="mr-0 md:mr-4 mb-2 md:mb-0 lg:self-center lg:p-2 flex-shrink-0 lg:mr-4">
                                    <span className="text-sm bg-pointed text-white rounded-full py-1 px-3 inline-flex max-w-full">
                                      {resource.sub_category}
                                    </span>
                                    <span className="block text-sm text-gray-500 mt-1">{resourceDate}</span>
                                  </div>
                                  <span className="text-lg font-semibold flex-grow">{resource.title}</span>
                                </Link>
                                <div className="flex items-center space-x-4 mt-2 md:mt-0">
                                  <SharePopover url={resource.link} />
                                  <Link to={resource.link} target="_blank">
                                    <ArrowUpIcon className="w-6 h-6 text-pointed cursor-pointer rotate-45 group-hover:rotate-90" />
                                  </Link>
                                </div>
                              </div>
                            )
                          })}
                        </div>
                      )
                    }
                  }}
                </Await>
              </Suspense>
            </>
          )}
        </Await>
      </Suspense>
    </section>
  )
}

export default Resources
